import * as React from 'react';
import { Box, Dialog, DialogContent, Tabs, Tab, FormControl, Select, MenuItem, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArtPlayer from './ArtPlayer';
import { SubtitleButtonGroup } from './SubtitleButtonGroup';
import { SubtitleTable } from './SubtitleTable';
import { AISummaryPanel } from './AISummaryPanel';
import config from '../../config';
import SubtitleApi from '../../api/SubtitleApi';
import { context as globalContext } from '../../context';
import { SubtitleDownloadButton } from '../clip/SubtitleDownloadButton';

export const SubtitleDialog = ({clip, clipGroup, initialSubtitles, status, setStatus, searchWord, initialTab = 0}) => {
    
    const [currentClip, setCurrentClip] = React.useState(clip);
    const [subtitles, setSubtitles] = React.useState(initialSubtitles || []);
    
    const [match, setMatch]             = React.useState(-1);
    const [selectedTab, setSelectedTab] = React.useState(initialTab);
    const [currentTime, setCurrentTime] = React.useState(0);

    const playerRef = React.useRef();
    const { onMessage } = React.useContext(globalContext);

    // 当 dialog 打开时，同步 selectedTab 到 initialTab
    React.useEffect(() => {
        if (status) {
            setSelectedTab(initialTab);
        }
    }, [status, initialTab]);

    React.useEffect(() => {
        if (currentClip.id === clip.id) {
            setSubtitles(initialSubtitles || []);
            return;
        }

        (async () => {
            try {
                const json = await SubtitleApi.findByClipId(currentClip.id, searchWord); 
                setSubtitles(json || []);
            } catch (ex) {
                onMessage({ type: 'error', content: ex.message || '获取字幕失败' });
                setSubtitles([]);
            }
        })();
    }, [currentClip, clip, initialSubtitles, onMessage, searchWord]);

    React.useEffect(() => {
        let firstMatch = -1;
        subtitles.forEach((subtitle, index) => {
            if (subtitle.matchMode === 1) {
                subtitle.markedContent = subtitle.markedContent
                                        .replaceAll('{', '<mark style="background-color: rgba(255, 139, 147, 0.2); color: #E67078; padding: 1px 2px; border-radius: 2px;">')
                                        .replaceAll('}', '</mark>')
                                        .replaceAll('[', '<mark style="background-color: rgba(255, 139, 147, 0.3); padding: 1px 2px; border-radius: 2px;">')
                                        .replaceAll(']', '</mark>');
            } else if (subtitle.matchMode === 2) {
                subtitle.markedContent = `<mark style="background-color: rgba(255, 139, 147, 0.15); padding: 1px 2px; border-radius: 2px;">${subtitle.markedContent}</mark>`;
            }
            if (subtitle.matchMode && firstMatch === -1) {
                firstMatch = index;
            }
        });
        setMatch(firstMatch);
    }, [subtitles]);

    const onClose = () => {
        setStatus(false);
        setCurrentClip(clip);
        setSubtitles([]);
        setMatch(-1);
        setSelectedTab(initialTab);
    }

    const onChangeTab = (e, newValue) => {
        setSelectedTab(newValue);
    }

    const onSeek = (time) => {
        if (currentClip.type === 1) {
            setCurrentTime(time);
        } else if (playerRef.current) {
            playerRef.current.currentTime = time;
        }
    }

    const handlePartChange = (event) => {
        const selectedClipId = event.target.value;
        const newClip = clipGroup.find(c => c.id === selectedClipId);
        if (newClip) {
            setCurrentClip(newClip);
        }
    };

    return (
        <Dialog 
            fullWidth={true} 
            maxWidth='xl' 
            open={status} 
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    maxHeight: '90vh',
                    m: { xs: 1, sm: 2 },
                    width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
                }
            }}
        >
            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    字幕详情
                </Typography>
                <IconButton 
                    onClick={onClose}
                    size="small"
                    sx={{ 
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ 
                display: 'flex', 
                p: { xs: 2, md: 3 },
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 3 },
            }}>
                {/* Left Panel - Subtitles */}
                <Box sx={{ 
                    display: 'flex', 
                    flex: { md: '0 0 55%' },
                    flexDirection: 'column',
                    minWidth: 0,
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        mb: 2,
                    }}>
                        <Tabs
                            value={selectedTab}
                            onChange={onChangeTab}
                            sx={{ 
                                flex: 1,
                                minHeight: 44,
                                '& .MuiTab-root': {
                                    minHeight: 44,
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                },
                            }}
                        >
                            <Tab label="全部字幕" />
                            <Tab label="匹配结果" />
                            <Tab 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />
                                        <span>AI 总结</span>
                                    </Box>
                                }
                            />
                        </Tabs>
                        <Box sx={{ px: 1 }}>
                            <SubtitleDownloadButton clip={currentClip} />
                        </Box>
                    </Box>
                    
                    <Box sx={{ 
                        flex: 1, 
                        overflow: 'hidden',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                    }}>
                        <div role='tabpanel' hidden={0 !== selectedTab} style={{ height: '100%' }}>
                            {selectedTab === 0 && (
                                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {match >= 0 && (
                                        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                            <SubtitleButtonGroup subtitles={subtitles} match={match} setMatch={setMatch} />
                                        </Box>
                                    )}
                                    <SubtitleTable match={match} subtitles={subtitles} onSeek={onSeek} /> 
                                </Box>
                            )}
                        </div>
                        <div role='tabpanel' hidden={1 !== selectedTab} style={{ height: '100%' }}>
                            {selectedTab === 1 && (
                                <SubtitleTable match={-1} subtitles={subtitles.filter(s => s.matchMode)} onSeek={onSeek} /> 
                            )}
                        </div>
                        <div role='tabpanel' hidden={2 !== selectedTab} style={{ height: '100%' }}>
                            {selectedTab === 2 && (
                                <AISummaryPanel clipId={currentClip.id} onSeek={onSeek} />
                            )}
                        </div>
                    </Box>
                </Box>

                {/* Right Panel - Video Player */}
                <Box sx={{ 
                    flex: { md: '0 0 45%' },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                    {/* Part Selector or Title */}
                    {clipGroup && clipGroup.length > 1 ? (
                        <FormControl fullWidth size="small">
                            <Select
                                value={currentClip.id}
                                onChange={handlePartChange}
                                variant="outlined"
                                sx={{
                                    borderRadius: '8px',
                                    '& .MuiSelect-select': {
                                        py: 1.25,
                                        fontSize: '0.875rem',
                                    },
                                }}
                            >
                                {[...clipGroup].sort((a, b) => (a.part || 1) - (b.part || 1)).map((part) => (
                                    <MenuItem key={part.id} value={part.id}>
                                        <Box component="span" sx={{ fontWeight: 600, mr: 1, color: 'primary.main' }}>
                                            P{part.part || 1}
                                        </Box>
                                        {part.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                lineHeight: 1.4,
                            }}
                        >
                            {currentClip.title}
                        </Typography>
                    )}
                    
                    {/* Video Player */}
                    <Box sx={{ 
                        borderRadius: '12px',
                        overflow: 'hidden',
                        bgcolor: '#000',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    }}>
                        {currentClip.type === 1 ? (
                            <iframe 
                                title={currentClip.title} 
                                width="100%" 
                                height="320" 
                                src={`//${currentClip.playUrl}&autoplay=true&t=${currentTime}`} 
                                scrolling="no" 
                                border="0" 
                                frameBorder="no" 
                                framespacing="0" 
                                allowFullScreen={true}
                            />
                        ) : (
                            <ArtPlayer
                                key={currentClip.id}
                                option={{
                                    url: `${currentClip.playUrl}`,
                                    poster: `//${currentClip.cover}`,
                                    subtitle: {
                                        url: `${config.url.api}/clips/${currentClip.id}/srt`,
                                        type: 'srt',
                                        encoding: 'utf-8',
                                        style: { color: '#FF8B93', 'font-size': '18px' },
                                    },
                                }}
                                style={{ width: '100%', height: '320px' }}
                                getInstance={(art) => playerRef.current = art}
                            />
                        )}
                    </Box>
                </Box>       
            </DialogContent>
        </Dialog>
    );
}